import { useState, useEffect } from "react";
import { useWebHelper } from "@viabl/echo-web-client-core";
import { Dialog, Fieldset, For, Heading, HStack, Separator, Tag, Text, useDisclosure, VStack } from '@chakra-ui/react';
import { useCache } from "../../context/CacheContext";
import { Button } from "../ui/button";
import { DialogBody, DialogContent, DialogFooter, DialogRoot, DialogTitle, DialogTrigger } from "../ui/dialog";
import { IAnnouncement } from "@/pages/announcements";
import { HiCloudUpload, HiOutlineBookmarkAlt } from "react-icons/hi";
import { HiOutlineMagnifyingGlass } from "react-icons/hi2";
import MarkdownPreview from '@uiw/react-markdown-preview';
import { useColorMode } from "../ui/color-mode";
import AnnouncementConfirmation from "./announcementConfirmation";
import { getColorName } from "./announcementEditor";

interface newUserProps {
    cb: Function,
    existingAnnouncement: IAnnouncement,
    intent: "archive" | "publish" | "view",
    disabled?: boolean
}

interface IDiscipline {
    id: string,
    name: string,
    skill_reference_id: string,
    target_type: number,
    accent: string,
}

// Creates a map to assign the names and accents to the discipline ids
const createDisciplineMap = (disciplines: IDiscipline[]) => {
    const map = new Map<string, IDiscipline>()
    disciplines.forEach(discipline => {
        map.set(discipline.id, discipline)
    })
    return map
}

function toNiceDate(iso: string) {
    const date = new Date(iso)

    const formatted = date.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });

    return formatted
}

export default function AnnouncementView(props: newUserProps) {
    const { onOpen, onClose } = useDisclosure();
    const [disciplineMap, setDisciplineMap] = useState<Map<string, IDiscipline>>(new Map())
    const [id, setId] = useState<string>()
    const [open, setOpen] = useState<boolean>(false);
    const { colorMode } = useColorMode();

    const webHelpers = useWebHelper();

    const announcement = props.existingAnnouncement

    const handleClickOpen = () => {
        onOpen();
        setOpen(true);
        setId(props.existingAnnouncement.id)

        webHelpers.GetAsync("/api/targetgroup/all", "iris-api").then((data: any) => {
            setDisciplineMap(createDisciplineMap(data))
        })
    }

    const handlePublish = () => {

        const body = {}

        webHelpers.PostAsync("/api/announcement/" + id + "/publish", "iris-api", body).then((data: any) => {
            handleClose()
            props.cb()
        })
    }

    const handleArchive = () => {

        const body = {}

        webHelpers.PostAsync("/api/announcement/" + id + "/archive", "iris-api", body).then((data: any) => {
            handleClose()
            props.cb()
        })
    }

    const handleClose = () => {
        setOpen(false);
        onClose();
    }

    return (
        <>
            <DialogRoot
                open={open}
                aria-describedby="alert-dialog-slide-description"
                size={{ base: "md", lg: "full" }}
            >
                <Dialog.Backdrop />
                <DialogTrigger>
                    <Button bg={"AbleBlue"} color={"white"} size={"sm"} marginLeft={{ base: "10px", lg: "30px" }}
                        disabled={props.disabled}
                        _hover={{ bg: "AbleYellow", color: "white" }}
                        _active={{ bg: "#ECB500" }}
                        onClick={handleClickOpen}
                    >
                        {props.intent === "publish" && (<><HiCloudUpload />{"Publish"}</>)}
                        {props.intent === "archive" && (<><HiOutlineBookmarkAlt />{"Archive"}</>)}
                        {props.intent === "view" && (<><HiOutlineMagnifyingGlass />{"View"}</>)}
                    </Button>
                </DialogTrigger>
                <DialogContent marginTop={"0px"} marginBottom={"0px"}>
                    {props.intent === "view" && <DialogTitle>View Announcement</DialogTitle>}
                    {props.intent === "archive" && <DialogTitle>Archive Announcement</DialogTitle>}
                    {props.intent === "publish" && <DialogTitle>Publish Announcement</DialogTitle>}
                    <DialogBody padding={"10px"}>
                        <Fieldset.Root p={10}>
                            <VStack gap={8} align={"left"}>
                                <Heading textStyle={"4xl"}>{announcement.title}</Heading>
                                {announcement.subtitle && <Text textStyle={"xl"}><strong>Subtitle: </strong>{announcement.subtitle}</Text>}
                                <HStack gap={4}>
                                    <Heading size={"xl"}>Target Groups: </Heading>
                                    <For each={announcement.target_groups}>
                                        {(target_groups) =>
                                            <Tag.Root size={"xl"} colorPalette={getColorName(disciplineMap.get(target_groups)?.accent || "gray")}>
                                                {/* Gets the name for the discipline or defaults to the id value on the announcement */}
                                                <Tag.Label fontSize={15}>{disciplineMap.get(target_groups)?.name || target_groups}</Tag.Label>
                                            </Tag.Root>
                                        }
                                    </For>
                                </HStack>
                                <Separator />
                                <Heading size={"xl"}>Message: </Heading>
                                <MarkdownPreview
                                    style={{
                                        padding: "0",
                                        background: "none",
                                    }}
                                    source={announcement.body}
                                    wrapperElement={{
                                        "data-color-mode": colorMode === "light" ? "light" : "dark",
                                    }}
                                />
                                <Separator />
                                <Text textStyle={"lg"}>Created: {toNiceDate(announcement.created)}</Text>
                                <Text textStyle={"lg"}>Last Modified: {toNiceDate(announcement.last_modified)}</Text>
                                <Text textStyle={"lg"}>Has Read Receipts: {announcement.is_acceptable ? "Yes" : "No"}</Text>
                            </VStack>
                        </Fieldset.Root>
                    </DialogBody>
                    <DialogFooter>
                        {props.intent === "view" && <></>}
                        {props.intent === "archive" && (<AnnouncementConfirmation intent={"archive"} cb={handleArchive} />)}
                        {props.intent === "publish" && (<AnnouncementConfirmation intent={"publish"} cb={handlePublish} />)}
                        <DialogTrigger>
                            <Button onClick={handleClose} colorScheme="red" marginLeft={"10px"}>Cancel</Button>
                        </DialogTrigger>
                    </DialogFooter>
                </DialogContent>
            </DialogRoot>
        </>
    );
}